import React, { useRef, useState, useMemo } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

/**
 * Props:
 *  - onVerified?: ({ id, token, data, url }) => void
 *  - onCancel?: () => void
 *  - closeOnSuccess?: boolean  // default true — close modal after success
 *
 * Notes:
 *  - This version DOES NOT write to Firestore from the client.
 *  - It calls the Cloud Function `generateSignedResumeUrl`, which:
 *      1) verifies reCAPTCHA
 *      2) writes to Firestore with Admin SDK
 *      3) returns a signed URL (and requestId)
 */
export default function ResumeRequestForm({
  onVerified,
  onCancel,
  closeOnSuccess = true,
}) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    hiringFor: "",
    message: "",
  });

  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errors, setErrors] = useState({});
  const [captchaValue, setCaptchaValue] = useState(null);

  const recaptchaRef = useRef(null);

  // convenience flags
  const nameProvided = formData.name.trim().length > 0;
  const emailProvided = formData.email.trim().length > 0;
  const companyProvided = formData.company.trim().length > 0;
  const messageLen = formData.message.trim().length;

  // Rule: message >= 50 unless name+email+company are all provided
  const meetsMessageRequirement =
    (nameProvided && emailProvided && companyProvided) || messageLen >= 50;

  const onChangeField = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!nameProvided) er.name = "Name is required.";
    if (!emailProvided) {
      er.email = "Email is required.";
    } else if (!/.+@.+\..+/.test(formData.email.trim())) {
      er.email = "Please enter a valid email.";
    }
    if (!meetsMessageRequirement) {
      er.message =
        "Please provide at least 50 characters (or include a company name).";
    }
    if (!captchaValue) er.captcha = "Please complete the CAPTCHA.";
    return er;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) return;

    setStatus("submitting");

    try {
      // ✅ Use your initialized Functions instance (us-central1) from ../firebase
      const generateSignedResumeUrl = httpsCallable(
        functions,
        "generateSignedResumeUrl"
      );

      const res = await generateSignedResumeUrl({
        ...formData,
        source: "resume-page",
        recaptchaToken: captchaValue,
      });

      const { url, requestId } = res?.data || {};

      if (!url) {
        throw new Error(
          "No URL returned from server. Check Cloud Function logs."
        );
      }

      setStatus("success");

      onVerified?.({
        id: requestId || null,
        token: captchaValue,
        data: { ...formData, source: "resume-page" },
        url,
      });

      if (closeOnSuccess) onCancel?.();

      // Reset the form & CAPTCHA
      setFormData({ name: "", email: "", company: "", message: "" });
      recaptchaRef.current?.reset();
      setCaptchaValue(null);
      setErrors({});
    } catch (err) {
      console.error("Error submitting resume request:", err);
      setStatus("error");
      alert(err?.message || "Submit failed. Check console for details.");
    }
  };

  const canAttemptSubmit = useMemo(() => {
    return (
      nameProvided && emailProvided && !!formData.hiringFor && !!captchaValue && status !== "submitting"
    );
  }, [nameProvided, emailProvided, captchaValue, status]);

  // CRA env var:
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  return (
    <form onSubmit={handleSubmit} className="resume-form" noValidate>
      <div className="resume-fields-grid">
        <div className="field full">
          <label className="label" htmlFor="rrf-name">
            Name *
          </label>
          <input
            id="rrf-name"
            name="name"
            placeholder="Your Name"
            value={formData.name}
            onChange={onChangeField}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "err-name" : undefined}
            required
          />
          {errors.name && (
            <div id="err-name" className="field-error">
              {errors.name}
            </div>
          )}
        </div>

        <div className="field full">
          <label className="label" htmlFor="rrf-email">
            Email *
          </label>
          <input
            id="rrf-email"
            name="email"
            type="email"
            placeholder="you@example.com"
            value={formData.email}
            onChange={onChangeField}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "err-email" : undefined}
            required
          />
          {errors.email && (
            <div id="err-email" className="field-error">
              {errors.email}
            </div>
          )}
        </div>

        <div className="field full">
          <label className="label" htmlFor="rrf-hiring">
            I am looking for *
          </label>
          <select
            id="rrf-hiring"
            name="hiringFor"
            value={formData.hiringFor}
            onChange={onChangeField}
            aria-invalid={!!errors.hiringFor}
            required
          >
          <option value="">Select one...</option>
          <option value="ae">Analytics Engineer</option>
          <option value="de">Data Engineer</option>
          <option value="bi">BI / Data Analyst</option>
          <option value="other">Other / General</option>
        </select>
        {errors.hiringFor && (
          <div className="field-error">{errors.hiringFor}</div>
        )}
        </div>

        <div className="field full">
          <label className="label" htmlFor="rrf-company">
            Company (optional)
          </label>
          <input
            id="rrf-company"
            name="company"
            placeholder="Company or Organization"
            value={formData.company}
            onChange={onChangeField}
          />
        </div>

        <div className="field full">
          <label className="label" htmlFor="rrf-message">
            Message or purpose{" "}
            {nameProvided && emailProvided && companyProvided
              ? "(company provided)"
              : "(min 50 chars)"}
          </label>
          <textarea
            id="rrf-message"
            name="message"
            rows={5}
            placeholder="Tell me a bit about the role, project, or how I can help…"
            value={formData.message}
            onChange={onChangeField}
            aria-invalid={!!errors.message}
            aria-describedby={errors.message ? "err-message" : "msg-count"}
          />
          {!(nameProvided && emailProvided && companyProvided) && (
            <div
              id="msg-count"
              className={`char-counter ${messageLen < 50 ? "need" : "ok"}`}
            >
              {messageLen}/50
            </div>
          )}
          {errors.message && (
            <div id="err-message" className="field-error">
              {errors.message}
            </div>
          )}
        </div>
      </div>

      <div className="recaptcha-box">
        <ReCAPTCHA
          ref={recaptchaRef}
          theme="dark"
          sitekey={recaptchaSiteKey}
          onChange={setCaptchaValue}
          onExpired={() => setCaptchaValue(null)}
        />
      </div>

      {errors.captcha && (
        <div className="field-error center">{errors.captcha}</div>
      )}

      <div className="resume-actions">
        <button
          type="button"
          className="resume-cancel"
          onClick={onCancel}
          disabled={status === "submitting"}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="resume-submit"
          disabled={!canAttemptSubmit}
        >
          {status === "submitting" ? "Submitting…" : "Request Resume"}
        </button>
      </div>

      {status === "success" && (
        <p className="ok-msg">Request received — preparing your resume…</p>
      )}
      {status === "error" && (
        <p className="error-msg">Something went wrong. Please try again.</p>
      )}
    </form>
  );
}










